import { components } from "@/api-schema";
import { BrandType, PathsV1BrandSearchPostParametersQuerySelect } from "@/api-schema-enums";
import { useBrandCreateMutation } from "@/common/api/brand/use-brand-create.mutation";
import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { createPlaceholderKeys } from "@/common/helpers/list-keys";
import { normalizeGeneratedOptionalString } from "@/common/helpers/normalize-generated-optional-string";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { FileUploader } from "@/components/file-uploader/file-uploader";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";
import { DAT_ID_INPUT_PATTERN, sanitizeDatIdInput } from "../../../_helpers/dat-id-input";
import { renderBrandType } from "../../_helpers/type-renderer";

type Brand = components["schemas"]["Brand"];

function getBrandDatId(brand: Brand): string | undefined {
  return normalizeGeneratedOptionalString(brand.datId);
}

export const BrandFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t("masterdata:NameIsRequired")),
    type: z.enum(BrandType),
    datId: z
      .string()
      .trim()
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: t("masterdata:DatIdMustBeValidPositiveNumber"),
      }),
    v1Id: z
      .string()
      .max(10, t("masterdata:V1IdMaxDigits"))
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: t("masterdata:V1IdMustBeValidPositiveNumber"),
      })
      .refine((val) => !val || parseInt(val, 10) <= 2147483647, {
        message: t("masterdata:V1IdMaxValue"),
      }),
    logo: z.instanceof(File).optional().nullable(),
  });

export type BrandFormSchemaType = z.infer<ReturnType<typeof BrandFormSchema>>;

export function BrandFormFields() {
  const { t } = useTranslation(["masterdata", "common"]);
  const [_, navigate] = useLocation();
  const { mutateAsync, isPending } = useBrandCreateMutation();
  const form = useForm<BrandFormSchemaType>({
    resolver: zodResolver(BrandFormSchema(t)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      type: undefined,
      datId: "",
      v1Id: "",
      logo: undefined,
    },
  });

  const [watchedName, watchedDatId, watchedV1Id] = useWatch({
    control: form.control,
    name: ["name", "datId", "v1Id"],
  });

  const { data: existingBrands, isPending: isExistingBrandsLoading } = useBrandSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1BrandSearchPostParametersQuerySelect.Uuid,
          PathsV1BrandSearchPostParametersQuerySelect.Name,
          PathsV1BrandSearchPostParametersQuerySelect.DatId,
          PathsV1BrandSearchPostParametersQuerySelect.Type,
          PathsV1BrandSearchPostParametersQuerySelect.V1Id,
        ],
      },
    },
  });

  const onSubmit = async (values: BrandFormSchemaType) => {
    try {
      await toastMutationStatus({
        mutationFn: () =>
          mutateAsync({
            name: values.name,
            type: values.type,
            datId: values.datId?.trim() || null,
            v1Id: values.v1Id?.trim() ? Number(values.v1Id) : null,
            logo: values.logo || undefined,
          }),
        successMessage: t("masterdata:BrandSuccessfullyCreated"),
        errorMessage: t("masterdata:ErrorCreatingBrand"),
      });

      navigate("~/masterdata/brand");
    } catch (error) {
      console.error("Error creating brand:", error);
    }
  };

  const duplicateValidation = useMemo(() => {
    if (!existingBrands?.data || !Array.isArray(existingBrands.data)) {
      return {
        hasErrors: false,
        hasDuplicateName: false,
        hasDuplicateDatId: false,
        hasDuplicateV1Id: false,
        errors: [] as string[],
      };
    }

    const errors: string[] = [];
    let hasDuplicateName = false;
    let hasDuplicateDatId = false;
    let hasDuplicateV1Id = false;

    if (watchedName?.trim()) {
      const duplicateName = existingBrands.data.find(
        (brand) => brand.name?.toLowerCase() === watchedName.toLowerCase().trim(),
      );
      if (duplicateName) {
        hasDuplicateName = true;
        errors.push(t("masterdata:BrandNameAlreadyExists", { name: watchedName }));
      }
    }

    if (watchedDatId?.trim()) {
      const duplicateDatId = existingBrands.data.find(
        (brand) => getBrandDatId(brand)?.toLowerCase() === watchedDatId.toLowerCase().trim(),
      );
      if (duplicateDatId) {
        hasDuplicateDatId = true;
        errors.push(t("masterdata:BrandDatIdAlreadyExists", { datId: watchedDatId }));
      }
    }

    if (watchedV1Id?.trim()) {
      const v1IdNumber = Number(watchedV1Id.trim());
      if (!Number.isNaN(v1IdNumber)) {
        const duplicateV1Id = existingBrands.data.find((brand) => brand.v1Id === v1IdNumber);
        if (duplicateV1Id) {
          hasDuplicateV1Id = true;
          errors.push(t("masterdata:BrandV1IdAlreadyExists", { v1Id: watchedV1Id }));
        }
      }
    }

    return {
      hasErrors: errors.length > 0,
      hasDuplicateName,
      hasDuplicateDatId,
      hasDuplicateV1Id,
      errors,
    };
  }, [existingBrands, watchedDatId, watchedName, watchedV1Id, t]);

  const handleSubmitWithValidation = (values: BrandFormSchemaType) => {
    if (duplicateValidation.hasErrors) {
      duplicateValidation.errors.forEach((error) => {
        toast.error(t("common:ValidationError"), {
          description: error,
          duration: 5000,
        });
      });
      return;
    }
    onSubmit(values);
  };

  const isLoading = isExistingBrandsLoading;
  const isSubmitDisabled = isPending || duplicateValidation.hasErrors || !form.formState.isValid;

  if (isLoading) {
    return (
      <div className="space-y-6">
        {createPlaceholderKeys(4, "skeleton").map((key, index) => (
          <div key={key} className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className={index === 4 ? "h-32 w-full rounded-md" : "h-10 w-full rounded-md"} />
          </div>
        ))}

        <div className="flex justify-end space-x-4">
          <Skeleton className="h-10 w-24 rounded-md" />
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <Card>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:Name")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("common:BrandNameExample")}
                        {...field}
                        error={duplicateValidation.hasDuplicateName}
                        disabled={isExistingBrandsLoading}
                      />
                      {isExistingBrandsLoading && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  {isExistingBrandsLoading ? (
                    <p className="text-muted-foreground mt-1 text-sm">{t("masterdata:CheckingDuplicateBrandNames")}</p>
                  ) : (
                    duplicateValidation.hasDuplicateName && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:BrandNameDuplicateError")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="datId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:DATID")}</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        placeholder={t("masterdata:DatIdExample")}
                        inputMode="numeric"
                        pattern={DAT_ID_INPUT_PATTERN}
                        {...field}
                        onChange={(event) => field.onChange(sanitizeDatIdInput(event.target.value))}
                        error={duplicateValidation.hasDuplicateDatId}
                        disabled={isExistingBrandsLoading}
                      />
                    </FormControl>
                    {isExistingBrandsLoading && (
                      <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                        <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                      </div>
                    )}
                  </div>
                  <FormMessage />
                  {isExistingBrandsLoading ? (
                    <p className="text-muted-foreground mt-1 text-sm">{t("masterdata:CheckingDuplicateDatIds")}</p>
                  ) : (
                    duplicateValidation.hasDuplicateDatId && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:BrandDatIdDuplicateError")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:Type")} *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("masterdata:SelectType")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(BrandType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {renderBrandType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="v1Id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:V1ID")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("masterdata:V1IdExample")}
                        {...field}
                        error={duplicateValidation.hasDuplicateV1Id}
                        disabled={isExistingBrandsLoading}
                      />
                      {isExistingBrandsLoading && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  {isExistingBrandsLoading ? (
                    <p className="text-muted-foreground mt-1 text-sm">{t("masterdata:CheckingDuplicateV1Ids")}</p>
                  ) : (
                    duplicateValidation.hasDuplicateV1Id && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:BrandV1IdDuplicateError")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="logo"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FileUploader
                      label={t("common:Logo")}
                      maxFiles={1}
                      value={field.value}
                      onFileSelect={(file) => field.onChange(file)}
                      onFileRemove={() => field.onChange(undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => window.history.back()}
          isPending={isPending}
          submitDisabled={isSubmitDisabled}
          submitLabel={t("common:Save")}
        />
      </form>
    </Form>
  );
}
